import type { SplitViewContentType } from '@saaga/shared/store/slices/split-view.slice'
import type { ReactNode } from 'react'

import { SplitViewContainer } from '@/components/split-view/split-view-container.organism'
import { ResizeHandle } from '@/components/ui/resize-handle.atom'
import { cn } from '@/lib/utils'

const MIN_PANEL_PERCENT = 30

interface DesktopLayoutProps {
  children: ReactNode
  isOpen: boolean
  contentType: SplitViewContentType
  contentData: Record<string, unknown> | null
  onClose: () => void
  className?: string
  containerRef: React.RefObject<HTMLDivElement | null>
  chatPercent: number
  onResize: (deltaX: number) => void
}

const getPanelStyles = (isOpen: boolean, chatPercent: number) => {
  const splitPercent = 100 - chatPercent
  const panelStyle = isOpen
    ? { flex: `${chatPercent} 0 0%`, minWidth: `${MIN_PANEL_PERCENT}%` }
    : undefined
  const splitStyle = isOpen
    ? { flex: `${splitPercent} 0 0%`, minWidth: `${MIN_PANEL_PERCENT}%` }
    : undefined
  return { panelStyle, splitStyle }
}

export const ChatSplitLayoutDesktop: React.FC<DesktopLayoutProps> = ({
  children,
  isOpen,
  contentType,
  contentData,
  onClose,
  className,
  containerRef,
  chatPercent,
  onResize,
}): ReactNode => {
  const { panelStyle, splitStyle } = getPanelStyles(isOpen, chatPercent)

  return (
    <div
      ref={containerRef}
      className={cn(
        'chat-split-layout chat-split-layout--desktop flex h-full min-h-0 overflow-hidden',
        className
      )}
    >
      <div
        className={cn(
          'chat-panel h-full min-h-0 overflow-hidden transition-[flex] duration-300 ease-out',
          isOpen ? '' : 'flex-1'
        )}
        style={panelStyle}
      >
        {children}
      </div>
      {isOpen && <ResizeHandle onResize={onResize} className="flex-shrink-0" />}
      <div
        className={cn(
          'split-view-panel h-full min-h-0 overflow-hidden bg-background transition-[flex,opacity] duration-300 ease-out',
          isOpen ? 'opacity-100' : 'flex-[0] min-w-0 opacity-0'
        )}
        style={splitStyle}
      >
        {isOpen && (
          <SplitViewContainer
            contentType={contentType}
            contentData={contentData}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  )
}
